import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, IconButton, Paper, Slider, Tooltip, Typography } from '@mui/material';
import GrassIcon from '@mui/icons-material/Grass';
import ReplayIcon from '@mui/icons-material/Replay';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import { InteractionMode, FLOCK_COLORS } from './Board.component';
import { SimState } from '../hooks/useSimulation';

// Species-specific fish icons matching the canvas rendering

const CyanFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 1.8} viewBox="-2 -10 26 20" style={{ display: 'block' }}>
    <polygon points="22,0 7,-5.5 0,0 7,5.5" fill={color} />
    <polygon points="11,-5.5 14,-9.5 17,-5.5" fill={color} />
    <circle cx="16" cy="-1.5" r="2" fill="white" />
    <circle cx="16.5" cy="-1.5" r="1.1" fill="#001a33" />
  </svg>
);

const OrangeFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 2.2} viewBox="-2 -12 26 24" style={{ display: 'block' }}>
    <polygon points="22,0 5,-7 0,0 5,7" fill={color} />
    <polygon points="11,6.5 8.5,11.5 3.5,6.5" fill={color} />
    <polygon points="7,-7 10,-11 14,-7" fill={color} />
    <circle cx="16" cy="-1.8" r="2" fill="white" />
    <circle cx="16.5" cy="-1.8" r="1.1" fill="#001a33" />
  </svg>
);

const GreenFishIcon = ({ color, size = 10 }: { color: string; size?: number }) => (
  <svg width={size * 2.4} height={size * 2} viewBox="-2 -11 26 22" style={{ display: 'block' }}>
    <polygon points="22,0 14,-3.5 4,-5.5 0,0 4,5.5 14,3.5" fill={color} />
    <polygon points="14,-3.5 17,-8 13,-3.8 11.5,-7.5 9,-4.5" fill={color} />
    <polygon points="11,5 9.5,9 6,5" fill={color} />
    <circle cx="17" cy="-1.5" r="2" fill="white" />
    <circle cx="17.5" cy="-1.5" r="1.1" fill="#001a33" />
  </svg>
);

const PredatorIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size * 2.8} height={size * 2.2} viewBox="-4 -13 30 26" style={{ display: 'block' }}>
    <polygon points="24,0 6,-6.5 -2,-2 -2,2 6,6.5" fill="#ff1744" />
    <polygon points="-2,-2 -8,-6 -2,0 -8,6 -2,2" fill="#b71c1c" />
    <polygon points="9,-6.5 13,-12 18,-6.5" fill="#b71c1c" />
    <polygon points="24,0 26,-2.5 22,-0.5" fill="white" />
    <polygon points="21.5,0 23.5,-2.5 19.5,-0.5" fill="white" />
    <circle cx="18" cy="-2.5" r="2.5" fill="#ffe600" />
    <circle cx="18.5" cy="-2.5" r="1.4" fill="#000" />
  </svg>
);

type Props = {
  stateRef: React.MutableRefObject<SimState | null>;
  mode: InteractionMode;
  onModeChange: (mode: InteractionMode) => void;
  onSpeedChange: (speed: number) => void;
  onReset: () => void;
};

const Controls = ({ stateRef, mode, onModeChange, onSpeedChange, onReset }: Props) => {
  const [fps, setFps] = useState(0);
  const [counts, setCounts] = useState({ flock0: 0, flock1: 0, flock2: 0, predators: 0 });
  const [speed, setSpeed] = useState(1);
  const frameTimesRef = useRef<number[]>([]);

  useEffect(() => {
    const id = setInterval(() => {
      const now = performance.now();
      frameTimesRef.current.push(now);
      frameTimesRef.current = frameTimesRef.current.filter(t => now - t < 1000);
      setFps(frameTimesRef.current.length);

      const boids = stateRef.current?.boids ?? [];
      setCounts({
        flock0: boids.filter(b => b.type === 'fish' && b.flock === 0).length,
        flock1: boids.filter(b => b.type === 'fish' && b.flock === 1).length,
        flock2: boids.filter(b => b.type === 'fish' && b.flock === 2).length,
        predators: boids.filter(b => b.type === 'predator').length,
      });
    }, 150);
    return () => clearInterval(id);
  }, [stateRef]);

  const toggle = (m: InteractionMode) => onModeChange(mode === m ? 'none' : m);

  const btnSx = (m: InteractionMode) => ({
    color: mode === m ? '#0a1628' : '#00e5ff',
    bgcolor: mode === m ? '#00e5ff' : 'transparent',
    border: '1px solid #00e5ff44',
    borderRadius: 1,
    '&:hover': { bgcolor: mode === m ? '#29b6f6' : '#00e5ff22' },
  });

  const fishBtnSx = (m: InteractionMode, color: string) => ({
    color: mode === m ? '#0a1628' : color,
    bgcolor: mode === m ? color : 'transparent',
    border: `1px solid ${color}44`,
    borderRadius: 1,
    p: 0.75,
    '&:hover': { bgcolor: mode === m ? color : `${color}22` },
  });

  const eraserSx = {
    color: mode === 'eraser' ? '#0a1628' : '#ff5252',
    bgcolor: mode === 'eraser' ? '#ff5252' : 'transparent',
    border: '1px solid #ff525244',
    borderRadius: 1,
    '&:hover': { bgcolor: mode === 'eraser' ? '#ff1744' : '#ff525222' },
  };

  const handleSpeedChange = (_: Event, v: number | number[]) => {
    const s = v as number;
    setSpeed(s);
    onSpeedChange(s);
  };

  const speciesRows = [
    { key: 'flock0', count: counts.flock0, color: FLOCK_COLORS[0], icon: <CyanFishIcon color={FLOCK_COLORS[0]} size={7} /> },
    { key: 'flock1', count: counts.flock1, color: FLOCK_COLORS[1], icon: <OrangeFishIcon color={FLOCK_COLORS[1]} size={7} /> },
    { key: 'flock2', count: counts.flock2, color: FLOCK_COLORS[2], icon: <GreenFishIcon color={FLOCK_COLORS[2]} size={7} /> },
    { key: 'pred', count: counts.predators, color: '#ff1744', icon: <PredatorIcon size={7} /> },
  ];

  return (
    <Paper elevation={6} sx={{
      position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
      bgcolor: '#0d1f35cc', backdropFilter: 'blur(8px)',
      border: '1px solid #1e3a5f', borderRadius: 2,
      p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center',
      minWidth: 72,
    }}>
      {/* Reset */}
      <Tooltip title="Reset simulation" placement="left">
        <IconButton onClick={onReset} size="small" sx={{
          color: '#ff9800', border: '1px solid #ff980044', borderRadius: 1,
          '&:hover': { bgcolor: '#ff980022' },
        }}>
          <ReplayIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: '100%', borderColor: '#1e3a5f' }} />

      {/* FPS */}
      <Typography sx={{ color: '#607d8b', fontSize: 10, fontFamily: 'monospace' }}>
        {fps} fps
      </Typography>

      {/* Species counts */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', width: '100%', pl: 0.5 }}>
        {speciesRows.map(({ key, count, icon }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {icon}
            <Typography sx={{ color: '#90a4ae', fontSize: 11, fontFamily: 'monospace' }}>
              {count}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ width: '100%', borderColor: '#1e3a5f' }} />

      {/* Speed slider */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 0.25 }}>
        <Typography sx={{ color: '#607d8b', fontSize: 10, fontFamily: 'monospace' }}>
          {speed}x
        </Typography>
        <Slider
          value={speed}
          onChange={handleSpeedChange}
          min={0.25} max={3} step={0.25}
          orientation="vertical"
          size="small"
          sx={{
            color: '#00e5ff', height: 60,
            '& .MuiSlider-thumb': { width: 10, height: 10 },
          }}
        />
      </Box>

      <Divider sx={{ width: '100%', borderColor: '#1e3a5f' }} />

      {/* Add fish species */}
      <Tooltip title="Add cyan fish — click canvas" placement="left">
        <IconButton onClick={() => toggle('addFish0')} sx={fishBtnSx('addFish0', FLOCK_COLORS[0])} size="small">
          <CyanFishIcon color={mode === 'addFish0' ? '#0a1628' : FLOCK_COLORS[0]} size={8} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add orange fish — click canvas" placement="left">
        <IconButton onClick={() => toggle('addFish1')} sx={fishBtnSx('addFish1', FLOCK_COLORS[1])} size="small">
          <OrangeFishIcon color={mode === 'addFish1' ? '#0a1628' : FLOCK_COLORS[1]} size={8} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add green fish — click canvas" placement="left">
        <IconButton onClick={() => toggle('addFish2')} sx={fishBtnSx('addFish2', FLOCK_COLORS[2])} size="small">
          <GreenFishIcon color={mode === 'addFish2' ? '#0a1628' : FLOCK_COLORS[2]} size={8} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add predator — click canvas" placement="left">
        <IconButton onClick={() => toggle('addPredator')} sx={btnSx('addPredator')} size="small">
          <PredatorIcon size={8} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Drop food — click canvas" placement="left">
        <IconButton onClick={() => toggle('addFood')} sx={btnSx('addFood')} size="small">
          <GrassIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Draw wall — drag on canvas" placement="left">
        <IconButton onClick={() => toggle('addWall')} sx={btnSx('addWall')} size="small">
          <HorizontalRuleIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Eraser — hold click to erase" placement="left">
        <IconButton onClick={() => toggle('eraser')} sx={eraserSx} size="small">
          <AutoFixNormalIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ height: 2 }} />
    </Paper>
  );
};

export default Controls;
